import { clampTo, hasImageInput } from '../../tools';
import type { KieModelConfig } from '../types';

export const qwenConfigs: Record<string, KieModelConfig> = {
    'qwen-image-3.0': {
        // https://docs.kie.ai/cn/market/qwen3/text-to-image
        // https://docs.kie.ai/cn/market/qwen3/image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'qwen3/image-to-image',
                    input: {
                        prompt: prompt.slice(0, 800),
                        image_size: clampTo(aspectRatio || '16:9', ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16', '21:9'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                        // 图片 URL
                        image_urls: image_urls!.slice(0, 3),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['png', 'jpeg'] as const,
                            'png',
                        ),
                        prompt_extend: typeof providerOptions?.prompt_extend === 'boolean'
                            ? providerOptions.prompt_extend
                            : true,
                        // system
                        nsfw_checker: false,
                    },
                };
            }
            return {
                model: 'qwen3/text-to-image',
                input: {
                    prompt: prompt.slice(0, 800),
                    image_size: clampTo(aspectRatio || '16:9', ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16', '21:9'] as const, '16:9'),
                    resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                    // 可选参数
                    output_format: clampTo(
                        providerOptions?.output_format || 'png',
                        ['png', 'jpeg'] as const,
                        'png',
                    ),
                    prompt_extend: typeof providerOptions?.prompt_extend === 'boolean'
                        ? providerOptions.prompt_extend
                        : true,
                    // system
                    nsfw_checker: false,
                },
            };
        },
    },
    'qwen-image-3.0-pro': {
        // https://docs.kie.ai/cn/market/qwen3-pro/text-to-image
        // https://docs.kie.ai/cn/market/qwen3-pro/image-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'qwen3/pro-image-to-image',
                    input: {
                        prompt: prompt.slice(0, 800),
                        image_size: clampTo(aspectRatio || '16:9', ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16', '21:9'] as const, '16:9'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                        // 图片 URL
                        image_urls: image_urls!.slice(0, 3),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['png', 'jpeg'] as const,
                            'png',
                        ),
                        prompt_extend: typeof providerOptions?.prompt_extend === 'boolean'
                            ? providerOptions.prompt_extend
                            : true,
                        // system
                        nsfw_checker: false,
                    },
                };
            }
            return {
                model: 'qwen3/pro-text-to-image',
                input: {
                    prompt: prompt.slice(0, 800),
                    image_size: clampTo(aspectRatio || '16:9', ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16', '21:9'] as const, '16:9'),
                    resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                    // 可选参数
                    output_format: clampTo(
                        providerOptions?.output_format || 'png',
                        ['png', 'jpeg'] as const,
                        'png',
                    ),
                    prompt_extend: typeof providerOptions?.prompt_extend === 'boolean'
                        ? providerOptions.prompt_extend
                        : true,
                    // system
                    nsfw_checker: false,
                },
            };
        },
    },
    'qwen-image-2.1': {
        // https://kie.ai/qwen-image-2.1?model=qwen2-1%2Ftext-to-image
        // https://kie.ai/qwen-image-2.1?model=qwen2-1%2Fimage-to-image
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    model: 'qwen2-1/image-to-image',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        aspect_ratio: aspectRatio === 'match'
                            ? 'auto'
                            : clampTo(aspectRatio || '1:1', ['1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'] as const, '1:1'),
                        resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                        // 图片 URL
                        image_urls: image_urls!.slice(0, 10),
                        // 可选参数
                        output_format: clampTo(
                            providerOptions?.output_format || 'png',
                            ['png', 'webp', 'jpeg'] as const,
                            'png',
                        ),
                        enhance_prompt: typeof providerOptions?.enhance_prompt === 'boolean'
                            ? providerOptions.enhance_prompt
                            : true,
                        seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                        // system
                        nsfw_checker: false,
                    },
                };
            }
            return {
                model: 'qwen2-1/text-to-image',
                input: {
                    prompt: prompt.slice(0, 5000),
                    aspect_ratio: clampTo(aspectRatio || '1:1', ['1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'] as const, '1:1'),
                    resolution: clampTo(image_resolution?.toUpperCase() || '1K', ['1K', '2K'] as const, '1K'),
                    // 可选参数
                    output_format: clampTo(
                        providerOptions?.output_format || 'png',
                        ['png', 'webp', 'jpeg'] as const,
                        'png',
                    ),
                    enhance_prompt: typeof providerOptions?.enhance_prompt === 'boolean'
                        ? providerOptions.enhance_prompt
                        : true,
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // system
                    nsfw_checker: false,
                },
            };
        },
    },
};